// Filing happens BEHIND the merchant, not in front of them.
//
// ---------------------------------------------------------------------------
// WHY THIS EXISTS
// ---------------------------------------------------------------------------
// Confirm used to block: upload four photographs, wait, then clear the deck and
// resume scanning. On shop wifi that is a few seconds of a person standing
// still in an aisle holding a product they have already photographed, several
// hundred times over an afternoon. The scanning is the work; the uploading is
// bookkeeping, and bookkeeping should not be on the critical path.
//
// So Confirm hands the capture to this queue and returns immediately. The deck
// clears, the camera goes back to reading barcodes, and the bytes go up while
// the merchant walks to the next product.
//
// ---------------------------------------------------------------------------
// THE HAZARD, AND WHAT PROTECTS AGAINST IT
// ---------------------------------------------------------------------------
// The blocking version cleared the deck ONLY after the commit returned, and its
// comment said why: "Clearing optimistically would lose a merchant's
// photographs on any failed upload — and the failure this page is most likely
// to hit is a bad shop wifi connection, which is exactly when they cannot be
// re-taken from where the person is standing."
//
// That reasoning did not go away, so this queue has to carry it:
//
//   1. THE BLOBS STAY HERE until the commit succeeds. Clearing the deck moves
//      them, it does not drop them. A failed upload still holds every byte.
//   2. FAILURES RETRY, with backoff, and then WAIT rather than disappearing.
//      A permanently failed item stays in the queue, visible and retryable, so
//      "it uploaded" is never assumed.
//   3. NOTHING IS EVER SILENTLY DISCARDED. There is no path in this file that
//      drops a capture's bytes without either a successful commit or the
//      merchant explicitly discarding it.
//   4. THE PAGE WARNS BEFORE UNLOAD while anything is outstanding, because a
//      closed tab is the one failure this cannot retry.
//
// Idempotency does the rest: `uploadId` is minted before the first byte moves
// and commit upserts on it, so a retry files the product once. A capture that
// uploaded its bytes but died before the commit is re-PUT to identical,
// content-addressed keys and committed once.

import { fileCapture, SessionExpired, newUploadId } from "./captureClient";

/** How many times to retry before parking an item for the merchant to see. */
const MAX_ATTEMPTS = 4;

/** Backoff between attempts. Long enough to outlast walking past a dead spot. */
const BACKOFF_MS = [1500, 5000, 15000];

/**
 * Refusals no retry can change. Parked at once rather than retried four times
 * over twenty seconds: the product was deleted, and it stays deleted (`target_gone`: the same, for a chosen item).
 */
const PERMANENT = new Set(["product_gone", "target_gone"]);

/**
 * How many captures may be waiting before Confirm pushes back.
 *
 * Bytes are held in memory until commit, so these are real ceilings, not
 * tidiness ones. Prod photos average 1.0 MB (p95 1.4 MB, 14 days to 10-07), so
 * a full 15-shot capture is ~15 MB. Six of those would be ~90 MB in a phone
 * tab; the byte cap keeps the worst case where six 8-shot captures had it.
 * One capture is always accepted, whatever its size.
 */
export const MAX_PENDING = 6;
export const MAX_PENDING_BYTES = 48 * 1024 * 1024;

const heldBytes = (item) => item.shots.reduce((n, s) => n + (s.blob?.size || 0), 0);

let nextId = 1;

/**
 * A queue bound to one page. `onChange` fires with a plain summary whenever
 * anything moves, so the UI never reaches into the internals.
 */
export function createUploadQueue({ onChange, onFiled, onExpired } = {}) {
  // Handlers are held in a box rather than closed over, so the screen that is
  // currently on top can take them and hand them back without the queue being
  // torn down. See sharedUploadQueue below for why that matters.
  let handlers = { onChange, onFiled, onExpired };
  let owner = null;
  /** @type {Array<{id:number, barcode:string, shots:Array, attempts:number, error:string|null}>} */
  const items = [];
  // Queue-level observers (the training sender), independent of handler ownership.
  const listeners = new Set();
  const emit = (kind, uploadId) =>
    listeners.forEach((l) => {
      try {
        l[kind]?.(uploadId);
      } catch {
        /* an observer never affects the queue */
      }
    });
  let running = false;
  let stopped = false;

  function summary() {
    return {
      pending: items.length,
      uploading: items.length > 0 && running,
      failed: items.filter((i) => i.error).length,
      // The barcode currently going up, so the UI can say what it is doing
      // rather than showing an anonymous spinner.
      current: running && items[0] ? items[0].label || items[0].barcode || null : null,
      // The parked head, so a screen waiting on ONE capture can tell its own
      // failure — and which failure — from someone else's.
      // `network`: parked by a lost connection, so waiting rather than refused.
      stuck: items[0]?.error ? { uploadId: items[0].uploadId, error: items[0].error, network: Boolean(items[0].network) } : null,
    };
  }

  const notify = () => handlers.onChange?.(summary());

  function releaseShots(item) {
    // Object URLs belong to the tiles that are already gone from the screen.
    // Revoking them here rather than at Confirm is what lets a failed item be
    // retried and, if the merchant asks, re-examined.
    item.shots.forEach((s) => {
      try {
        if (s.url) URL.revokeObjectURL(s.url);
      } catch {
        /* best-effort */
      }
    });
  }

  async function pump() {
    if (running || stopped) return;
    running = true;
    notify();

    while (items.length > 0 && !stopped) {
      const item = items[0];
      try {
        const result = await fileCapture({
          barcode: item.barcode,
          target: item.target,
          recapture: item.recapture,
          shots: item.shots,
          // THE SAME ID ON EVERY ATTEMPT. Minted once when the capture was
          // handed over, so a retry re-PUTs to the same content-addressed keys
          // and commits the same row instead of starting a fresh prefix and
          // orphaning the last one in S3.
          uploadId: item.uploadId,
          productBound: item.productBound,
          // Photos already PUT by an earlier attempt are not sent again.
          uploaded: item.uploaded,
        });
        // Committed. Only now do the bytes go.
        items.shift();
        releaseShots(item);
        item.error = null;
        emit("filed", item.uploadId);
        handlers.onFiled?.({
          barcode: item.barcode,
          label: item.label,
          code: result?.variantCode || result?.code || null,
          count: item.shots.length,
          // What the server says it stored for this commit; null when it did not say.
          stored: Number.isFinite(result?.stored) ? result.stored : null,
          result,
          uploadId: item.uploadId,
        });
        notify();
      } catch (err) {
        // A dead session is not a transient failure and must not be retried
        // into a loop — every attempt would 401. Stop, keep everything, and
        // let the app show the door.
        if (err instanceof SessionExpired) {
          item.error = "signed_out";
          running = false;
          notify();
          handlers.onExpired?.();
          return;
        }

        // ⚠️ SAY SOMETHING. A retry that logs nothing is indistinguishable
        // from a slow upload: the merchant sees "uploading" and so does
        // anyone reading the console, while the same exception is thrown four
        // times in a row. This cost an hour of staring at a queue that was
        // never going to drain.
        console.warn(
          `[capture] upload attempt ${item.attempts + 1} failed for ${item.label || item.barcode}:`,
          err,
        );

        item.attempts += 1;
        // Offline: park without burning the schedule; `online` resumes it.
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        // A network-shaped failure (fetch rejected, timed out) as opposed to
        // a server answer such as a 5xx: only the former resumes on visibility.
        item.network =
          offline || ["TypeError", "AbortError", "TimeoutError"].includes(err?.name);
        if (offline || item.attempts >= MAX_ATTEMPTS || PERMANENT.has(err?.message)) {
          // PARKED, NOT DROPPED. It stays at the head of the queue holding its
          // bytes; the UI shows it and the merchant can retry. Moving it aside
          // to let later captures through would reorder a merchant's work and
          // hide the failure behind successes.
          item.error = err?.message || "upload_failed";
          running = false;
          notify();
          return;
        }

        item.error = null;
        const wait = BACKOFF_MS[Math.min(item.attempts - 1, BACKOFF_MS.length - 1)];
        await new Promise((r) => setTimeout(r, wait));
      }
    }

    running = false;
    notify();
  }

  return {
    /**
     * Hand over a capture. Returns false when the queue is full, which is the
     * caller's cue to make the merchant wait rather than silently growing.
     */
    add({ barcode = null, target = null, recapture = false, label = null, shots, productBound = false, uploadId }) {
      if (items.length >= MAX_PENDING) return false;
      const held = items.reduce((n, it) => n + heldBytes(it), 0);
      if (items.length > 0 && held + heldBytes({ shots }) > MAX_PENDING_BYTES) return false;
      items.push({
        id: nextId++,
        // Minted HERE rather than per attempt — see the uploadId note in pump.
        // A caller may mint it instead, to recognise its own capture in
        // onFiled; either way it is fixed once and survives every retry.
        uploadId: uploadId || newUploadId(),
        barcode,
        target,
        recapture,
        label,
        shots,
        productBound,
        uploaded: new Set(),
        attempts: 0,
        error: null,
      });
      notify();
      void pump();
      return true;
    },

    /** Try a parked item again, now. */
    retry() {
      const head = items[0];
      if (!head) return;
      head.attempts = 0;
      head.error = null;
      notify();
      void pump();
    },

    /**
     * Resume a parked item when the connection may be back (`online`, page
     * visible). Never while offline, and never for a refusal a retry can't fix.
     * `networkOnly`: resume only a head parked by a network failure, not a 5xx.
     */
    resumeParked({ networkOnly = false } = {}) {
      const head = items[0];
      if (!head?.error || running || stopped) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      if (head.error === "signed_out" || PERMANENT.has(head.error)) return;
      if (networkOnly && !head.network) return;
      head.attempts = 0;
      head.error = null;
      notify();
      void pump();
    },

    /**
     * Give up on the parked item and lose its photographs.
     *
     * The ONLY path in this file that discards bytes, and it exists because the
     * alternative is a merchant stuck behind one capture that will never
     * succeed. It is deliberately explicit, deliberately one item, and the UI
     * must confirm it — nothing calls this on the merchant's behalf.
     */
    discardFailed() {
      const head = items[0];
      if (!head || !head.error) return;
      items.shift();
      releaseShots(head);
      emit("dropped", head.uploadId);
      notify();
      void pump();
    },

    /** Observe commits and discards: { filed(uploadId), dropped(uploadId) }. Returns an unsubscribe. */
    listen(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },

    summary,

    /** Everything still owed, for the unload guard. */
    outstanding: () => items.length,

    /**
     * Come back after a stop.
     *
     * ⚠️ THIS EXISTS BECAUSE OF STRICTMODE, and the bug it fixes was invisible
     * in production. React runs effects mount -> cleanup -> mount in
     * development, so a component that stops this queue in an effect cleanup
     * stopped it for good: the first cleanup fired milliseconds after the first
     * mount, `stopped` latched true, and every capture handed over afterwards
     * sat in the queue reporting "1 pending" forever while nothing was ever
     * sent. Production does not double-invoke, so the built app was fine and
     * only the lab was broken — the worst shape of bug to chase, because the
     * thing you are testing on is the only thing that has it.
     *
     * Idempotent, and it picks up whatever was already waiting.
     */
    resume() {
      if (!stopped) return;
      stopped = false;
      void pump();
    },

    stop() {
      stopped = true;
    },

    /**
     * Point the queue at whichever screen is currently showing.
     *
     * Returns a RELEASE function, and the ownership check in it is the whole
     * point. React tears the old screen down after the new one has already
     * claimed the handlers, so a cleanup that blindly cleared them wiped the
     * handlers of the screen that had just taken over — which is exactly how
     * the camera's "Uploading N in the background" row went silent the moment
     * a gallery unmounted behind it.
     *
     * Releasing only clears if you are still the owner. A screen that has been
     * superseded releases nothing.
     */
    setHandlers(next = {}) {
      const token = {};
      handlers = next;
      owner = token;
      return () => {
        if (owner !== token) return;
        handlers = {};
        owner = null;
      };
    },
  };
}

/**
 * ONE QUEUE FOR THE LIFE OF THE PAGE.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT PER-SCREEN, AND WHAT IT COST WHEN IT WAS
 * ---------------------------------------------------------------------------
 * The queue used to be a `useRef` inside CaptureCamera, stopped in that
 * component's effect cleanup. Both halves of that are wrong the moment a person
 * navigates:
 *
 *   - unmounting ran `stop()`, so the pump exited mid-flight
 *   - and the ref went with the component, taking the pending items AND THE
 *     PHOTOGRAPHS THEY WERE HOLDING with it
 *
 * So tapping back, or moving to the next item in a way that unmounted the
 * camera, did not pause an upload — it destroyed it. Re-entering built a fresh
 * empty queue, and the person saw a capture that said "uploading" and then
 * simply never appeared.
 *
 * Measured on krystals-express 2026-09-11: 19 uploads had bytes in S3 and only
 * 11 had rows. The stranded ones were part-uploaded — several with one
 * photograph where a capture carries two to four — which is what a queue dying
 * between PUTs looks like from the outside.
 *
 * The file's own promise at the top is "NOTHING IS EVER SILENTLY DISCARDED".
 * That was true of every path inside this file and untrue of the component that
 * owned it, which is the more expensive way to be wrong.
 *
 * So the queue lives at module scope. It is created once, survives every
 * navigation, and the screen on top borrows its handlers.
 */
let shared = null;

export function sharedUploadQueue() {
  if (!shared) {
    shared = createUploadQueue({});

    // ⚠️ THE UNLOAD GUARD BELONGS TO THE QUEUE, NOT TO A SCREEN.
    //
    // It used to be registered by CaptureCamera and torn down when that
    // component unmounted. That was consistent only while unmounting also
    // destroyed the queue: there was nothing left to warn about. Now the queue
    // survives, so a person who confirms two captures, taps back to the
    // gallery and then closes the tab lost the photographs with NO dialog —
    // the one failure a retry cannot fix, and the file's own promise #4 at the
    // top of this module.
    //
    // Registered once, here, off `outstanding()`. Whichever screen is showing
    // is irrelevant to whether work is outstanding.
    // Feature-checked rather than environment-checked: a test harness can
    // define `window` without defining addEventListener, and this module must
    // load anywhere it is imported.
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      window.addEventListener("beforeunload", (e) => {
        if (shared.outstanding() === 0) return undefined;
        e.preventDefault();
        // Browsers ignore custom text now and show their own wording;
        // returnValue is still what makes the dialog appear at all.
        e.returnValue = "";
        return "";
      });
      window.addEventListener("online", () => shared.resumeParked());
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") shared.resumeParked({ networkOnly: true });
        });
      }
    }
  }
  return shared;
}
